// PRP-FEAT-01–04 — Proposal Preparation & Generation, migrated to the section-based schema
// (`proposal structure and schema.md`).
// Backend: api-service app/api/v1/endpoints/proposals.py, app/domain/proposals/models.py
//
// The proposal document itself is created/advanced/locked by workflow module_action hooks
// (proposals.prepareOrAdvance / proposals.lockOnApproval — app.domain.workflows.actions.
// proposal_actions), fired by workflow transitions — there is still no REST endpoint to
// create/advance/approve a proposal directly. What DOES exist: reading the current version,
// editing one section instance while still draft-editable, reordering which snapshotted option
// is "recommended", downloading the generated PDF, and the platform/tenant reference data
// (templates, section definitions, payment terms, term clauses, exclusions, marketing
// references) a proposal's sections are built from.
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '@/redux/common/baseQuery';

interface ApiResponse<T> {
  success: boolean;
  message_code: number;
  message: string;
  data: T;
}

export type SectionType =
  | 'COMPANY_BRANDING' | 'CUSTOMER_PROPERTY' | 'PROPOSAL_INFORMATION' | 'COVER_LETTER'
  | 'ASSESSMENT_SUMMARY' | 'PHOTOS' | 'SCOPE_OF_WORK' | 'MATERIALS' | 'PRICING_OPTIONS'
  | 'TIMELINE' | 'WARRANTY' | 'PREVIOUS_WORK_REFERENCES' | 'PAYMENT_TERMS' | 'EXCLUSIONS'
  | 'TERMS_CONDITIONS' | 'FREE_FORM' | 'ACCEPTANCE';
export type ContentMode = 'STATIC' | 'DATA_BOUND' | 'AI_GENERATED' | 'SELECTED_CONTENT' | 'CALCULATED' | 'USER_AUTHORED';
export type PricingPresentationMode = 'SINGLE_TOTAL' | 'COST_TYPE_SUMMARY' | 'CUSTOMER_CATEGORY_SUMMARY';

export interface ProposalSectionDefinition {
  section_definition_code: string;
  section_type: SectionType;
  section_name: string;
  default_title?: string | null;
  content_mode: ContentMode;
  supported_source_types: string[];
  allow_multiple_instances: boolean;
  allow_user_edit: boolean;
  allow_user_remove: boolean;
  allow_regeneration: boolean;
  active: boolean;
}

export interface ProposalTemplateSection {
  section_definition_code: string;
  display_order: number;
  included_by_default: boolean;
  required: boolean;
  allow_user_remove: boolean;
  allow_user_edit: boolean;
  section_title_override?: string | null;
}

export interface ProposalTemplate {
  proposal_template_code: string;
  proposal_template_name: string;
  version: string;
  description?: string | null;
  applicable_engagement_types: string[];
  sections: ProposalTemplateSection[];
  default_pricing_presentation_mode: PricingPresentationMode;
  active: boolean;
}

export interface PaymentScheduleMilestone {
  milestone: string;
  percentage: number;
}
export interface PaymentTerm {
  payment_term_code: string;
  payment_term_version: number;
  name: string;
  payment_schedule: PaymentScheduleMilestone[];
  description_text?: string | null;
  default: boolean;
  active: boolean;
}

export interface TermClause {
  clause_code: string;
  clause_version: number;
  category: string;
  text: string;
  active: boolean;
}

export interface ExclusionItem {
  exclusion_code: string;
  exclusion_version: number;
  text: string;
  active: boolean;
}

/** A warranty term the tenant OFFERS on proposals — the WARRANTY section starts from the
 *  entries marked `default`. Not the same thing as a warranty ISSUED on a property once a job is
 *  done (that is `/properties/{id}/warranties`). */
export interface WarrantyTerm {
  warranty_term_code: string;
  warranty_term_version: number;
  title: string;
  duration: string;
  description?: string | null;
  default: boolean;
  active: boolean;
}

export interface MarketingReference {
  reference_id: string;
  title: string;
  project_type?: string | null;
  location?: string | null;
  completion_year?: number | null;
  scope_summary?: string | null;
  product_or_manufacturer?: string | null;
  description?: string | null;
  photo_attachment_ids: string[];
  testimonial?: string | null;
  approved_for_marketing: boolean;
  active: boolean;
}

export interface EstimateOptionRef {
  option_id: string;
  estimate_id: string;
  estimate_option_version: number;
  /** The option's name, snapshotted — an excluded option is absent from the pricing section, so
   *  this is the only place its name survives. */
  display_label?: string | null;
  /** Whether the client sees this option on the proposal. Every completed option is snapshotted;
   *  the Sales Agent picks which ones to actually send. */
  included: boolean;
}

export interface SourceReference {
  source_type: string;
  source_id: string;
  source_version?: string | number | null;
}

export interface SectionContent {
  structured_data?: Record<string, unknown> | null;
  generated_text?: string | null;
}

export interface ProposalSection {
  proposal_section_id: string;
  section_definition_code: string;
  section_type: SectionType;
  section_title?: string | null;
  content_mode: ContentMode;
  display_order: number;
  included: boolean;
  /** Snapshotted from the template at generation time — mirrors the approved mockup's own
   *  Hide/Show/Required per-section control. `required` (or `allow_user_remove: false`) means
   *  this section can never be hidden on this proposal. */
  required: boolean;
  allow_user_remove: boolean;
  /** Snapshotted from the template too — false for sections that only mirror data owned
   *  elsewhere (branding, customer, proposal meta), which stay read-only here. */
  allow_user_edit: boolean;
  content: SectionContent;
  source_references: SourceReference[];
  user_modified: boolean;
}

export interface ProposalCustomer {
  contact_id?: string | null;
  display_name?: string | null;
}

export interface ProposalRevision {
  previous_proposal_version?: number | null;
  revision_reason?: string | null;
  revision_source?: 'INTERNAL' | 'REVIEWER' | 'CUSTOMER' | null;
}

export interface ProposalOut {
  proposal_id: string;
  proposal_number: string;
  proposal_version: number;
  engagement_id: string;
  property_id?: string | null;
  estimate_set_id: string;
  estimate_options: EstimateOptionRef[];
  proposal_template_code: string;
  proposal_template_version: string;
  currency: string;
  proposal_date?: string | null;
  valid_until?: string | null;
  customer: ProposalCustomer;
  sections: ProposalSection[];
  revision: ProposalRevision;
  created_by: string;
  created_by_name?: string | null;
  created_at: string;
  updated_at: string;
  // "DRAFT" | "IN_REVIEW" | "REVISION_REQUESTED" | "APPROVED" | "SENT" | "ACCEPTED" | "REJECTED"
  status: string;
  /** Decided by the backend (ProposalService._DRAFT_EDITABLE_STATUSES), not re-derived here.
   *  `status` alone cannot answer it: a draft being written at Assessment Completed and one
   *  already submitted for review both derive to "DRAFT", and only the first is editable. */
  is_editable: boolean;
  returned_reason?: string | null;
  returned_by_name?: string | null;
  returned_on?: string | null;
}

export interface UpdateProposalSectionInput {
  engagementId: string;
  proposalSectionId: string;
  included?: boolean;
  section_title?: string;
  structured_data?: Record<string, unknown>;
  generated_text?: string;
  /** New position for this section within the document — sent on drag-and-drop reorder. */
  display_order?: number;
}

export interface SetRecommendedOptionInput {
  engagementId: string;
  option_id: string;
}

export interface SetOptionIncludedInput {
  engagementId: string;
  option_id: string;
  included: boolean;
}

export interface ChangeProposalTemplateInput {
  engagementId: string;
  proposal_template_code: string;
}

export interface CreatePaymentTermInput {
  name: string;
  payment_schedule: PaymentScheduleMilestone[];
  description_text?: string;
  default?: boolean;
}

/** Partial by design — the Settings rows PATCH one field (a toggle, a reworded clause) rather
 *  than resubmitting the whole record. Mirrors the backend's Update*Request models. */
export interface UpdatePaymentTermInput {
  payment_term_code: string;
  name?: string;
  payment_schedule?: PaymentScheduleMilestone[];
  description_text?: string;
  default?: boolean;
  active?: boolean;
}

export interface CreateTermClauseInput {
  category: string;
  text: string;
}

export interface UpdateTermClauseInput {
  clause_code: string;
  category?: string;
  text?: string;
  active?: boolean;
}

export interface CreateExclusionInput {
  text: string;
}

export interface UpdateExclusionInput {
  exclusion_code: string;
  text?: string;
  active?: boolean;
}

export interface CreateWarrantyTermInput {
  title: string;
  duration: string;
  description?: string;
  default?: boolean;
}

export interface UpdateWarrantyTermInput {
  warranty_term_code: string;
  title?: string;
  duration?: string;
  description?: string;
  default?: boolean;
  active?: boolean;
}

export interface CreateMarketingReferenceInput {
  title: string;
  project_type?: string;
  location?: string;
  completion_year?: number;
  scope_summary?: string;
  product_or_manufacturer?: string;
  description?: string;
  photo_attachment_ids?: string[];
  testimonial?: string;
  approved_for_marketing?: boolean;
}

/** One row of the Client Accepted dropdown — an option the client was actually shown (the
 *  backend leaves out options excluded from the sent document, which can never be the accepted
 *  one). Mirrors app/domain/proposals/service.py ProposalService.get_acceptance. */
export interface ProposalAcceptanceOption {
  option_id: string;
  estimate_id?: string | null;
  estimate_option_version?: number | null;
  display_label?: string | null;
}

export interface ProposalAcceptance {
  proposal_id: string;
  proposal_number?: string | null;
  /** The ACCEPTANCE section's OWN flag (set from the option count when the document was built) —
   *  do not re-derive it from `options.length` here: the document decides whether the client was
   *  offered a choice, not this bundle. */
  option_selection_required: boolean;
  accepted_option: { option_id?: string | null; estimate_id?: string | null; estimate_option_version?: number | null };
  accepted_at?: string | null;
  accepted_by_name?: string | null;
  options: ProposalAcceptanceOption[];
}

export const proposalsApi = createApi({
  reducerPath: 'proposalsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Proposal', 'ProposalMasters'],
  endpoints: (builder) => ({
    getCurrentProposal: builder.query<ProposalOut | null, string>({
      query: (engagementId) => `/api/v1/engagements/${engagementId}/proposals/current`,
      transformResponse: (res: ApiResponse<ProposalOut | null>) => res.data,
      providesTags: (_result, _e, engagementId) => [{ type: 'Proposal' as const, id: engagementId }],
    }),
    updateProposalSection: builder.mutation<ProposalOut, UpdateProposalSectionInput>({
      query: ({ engagementId, proposalSectionId, ...body }) => ({
        url: `/api/v1/engagements/${engagementId}/proposals/current/sections/${proposalSectionId}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (res: ApiResponse<ProposalOut>) => res.data,
      invalidatesTags: (_r, _e, { engagementId }) => [{ type: 'Proposal' as const, id: engagementId }],
    }),
    getProposalAcceptance: builder.query<ProposalAcceptance | null, string>({
      query: (engagementId) => `/api/v1/engagements/${engagementId}/proposals/current/acceptance`,
      transformResponse: (res: ApiResponse<ProposalAcceptance | null>) => res.data,
      providesTags: (_r, _e, engagementId) => [{ type: 'Proposal' as const, id: engagementId }],
    }),
    // `option_id` omitted = the single-option case; the backend records the one option on the
    // document and refuses to guess when there is more than one.
    recordProposalAcceptance: builder.mutation<
      { option_id?: string } | null,
      { engagementId: string; option_id?: string }
    >({
      query: ({ engagementId, option_id }) => ({
        url: `/api/v1/engagements/${engagementId}/proposals/current/acceptance`,
        method: 'POST',
        body: { option_id },
      }),
      transformResponse: (res: ApiResponse<{ option_id?: string } | null>) => res.data,
      invalidatesTags: (_r, _e, { engagementId }) => [{ type: 'Proposal' as const, id: engagementId }],
    }),
    setRecommendedOption: builder.mutation<ProposalOut, SetRecommendedOptionInput>({
      query: ({ engagementId, option_id }) => ({
        url: `/api/v1/engagements/${engagementId}/proposals/current/recommended-option`,
        method: 'POST',
        body: { option_id },
      }),
      transformResponse: (res: ApiResponse<ProposalOut>) => res.data,
      invalidatesTags: (_r, _e, { engagementId }) => [{ type: 'Proposal' as const, id: engagementId }],
    }),
    setOptionIncluded: builder.mutation<ProposalOut, SetOptionIncludedInput>({
      query: ({ engagementId, option_id, included }) => ({
        url: `/api/v1/engagements/${engagementId}/proposals/current/options/${option_id}`,
        method: 'PATCH',
        body: { included },
      }),
      transformResponse: (res: ApiResponse<ProposalOut>) => res.data,
      invalidatesTags: (_r, _e, { engagementId }) => [{ type: 'Proposal' as const, id: engagementId }],
    }),
    // Re-cuts the current DRAFT proposal onto a different template: same proposal number and
    // version, same snapshotted estimate options, new section set (hand-edited sections are
    // carried over server-side). Rejected by the backend once the proposal is no longer a draft.
    changeProposalTemplate: builder.mutation<ProposalOut, ChangeProposalTemplateInput>({
      query: ({ engagementId, proposal_template_code }) => ({
        url: `/api/v1/engagements/${engagementId}/proposals/current/template`,
        method: 'PATCH',
        body: { proposal_template_code },
      }),
      transformResponse: (res: ApiResponse<ProposalOut>) => res.data,
      invalidatesTags: (_r, _e, { engagementId }) => [{ type: 'Proposal' as const, id: engagementId }],
    }),
    // Real generated PDF (api-service app/domain/proposals/pdf.py via reportlab) — a Blob, not
    // JSON, so this bypasses the ApiResponse<T> envelope every other endpoint here uses.
    getProposalPdf: builder.query<Blob, string>({
      query: (engagementId) => ({
        url: `/api/v1/engagements/${engagementId}/proposals/current/pdf`,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    listProposalTemplates: builder.query<ProposalTemplate[], void>({
      query: () => '/api/v1/proposals/templates',
      transformResponse: (res: ApiResponse<ProposalTemplate[]>) => res.data,
      providesTags: ['ProposalMasters'],
    }),
    listProposalSectionDefinitions: builder.query<ProposalSectionDefinition[], void>({
      query: () => '/api/v1/proposals/section-definitions',
      transformResponse: (res: ApiResponse<ProposalSectionDefinition[]>) => res.data,
      providesTags: ['ProposalMasters'],
    }),
    // `includeInactive` is what separates the two readers of these catalogues: the Settings
    // screen shows deactivated entries so they can be switched back on, the in-proposal pickers
    // ask for live entries only.
    listPaymentTerms: builder.query<PaymentTerm[], { includeInactive?: boolean } | void>({
      query: (arg) => `/api/v1/proposals/payment-terms${arg?.includeInactive ? '?include_inactive=true' : ''}`,
      transformResponse: (res: ApiResponse<PaymentTerm[]>) => res.data,
      providesTags: ['ProposalMasters'],
    }),
    createPaymentTerm: builder.mutation<PaymentTerm, CreatePaymentTermInput>({
      query: (body) => ({ url: '/api/v1/proposals/payment-terms', method: 'POST', body }),
      transformResponse: (res: ApiResponse<PaymentTerm>) => res.data,
      invalidatesTags: ['ProposalMasters'],
    }),
    updatePaymentTerm: builder.mutation<PaymentTerm, UpdatePaymentTermInput>({
      query: ({ payment_term_code, ...body }) => ({
        url: `/api/v1/proposals/payment-terms/${payment_term_code}`, method: 'PATCH', body,
      }),
      transformResponse: (res: ApiResponse<PaymentTerm>) => res.data,
      invalidatesTags: ['ProposalMasters'],
    }),
    // Soft delete server-side: a proposal already built from this term keeps the copy it
    // snapshotted, so removing it here only takes it out of the pickers.
    deletePaymentTerm: builder.mutation<void, string>({
      query: (code) => ({ url: `/api/v1/proposals/payment-terms/${code}`, method: 'DELETE' }),
      invalidatesTags: ['ProposalMasters'],
    }),
    listTermClauses: builder.query<TermClause[], { includeInactive?: boolean } | void>({
      query: (arg) => `/api/v1/proposals/term-clauses${arg?.includeInactive ? '?include_inactive=true' : ''}`,
      transformResponse: (res: ApiResponse<TermClause[]>) => res.data,
      providesTags: ['ProposalMasters'],
    }),
    createTermClause: builder.mutation<TermClause, CreateTermClauseInput>({
      query: (body) => ({ url: '/api/v1/proposals/term-clauses', method: 'POST', body }),
      transformResponse: (res: ApiResponse<TermClause>) => res.data,
      invalidatesTags: ['ProposalMasters'],
    }),
    updateTermClause: builder.mutation<TermClause, UpdateTermClauseInput>({
      query: ({ clause_code, ...body }) => ({
        url: `/api/v1/proposals/term-clauses/${clause_code}`, method: 'PATCH', body,
      }),
      transformResponse: (res: ApiResponse<TermClause>) => res.data,
      invalidatesTags: ['ProposalMasters'],
    }),
    deleteTermClause: builder.mutation<void, string>({
      query: (code) => ({ url: `/api/v1/proposals/term-clauses/${code}`, method: 'DELETE' }),
      invalidatesTags: ['ProposalMasters'],
    }),
    listExclusions: builder.query<ExclusionItem[], { includeInactive?: boolean } | void>({
      query: (arg) => `/api/v1/proposals/exclusions${arg?.includeInactive ? '?include_inactive=true' : ''}`,
      transformResponse: (res: ApiResponse<ExclusionItem[]>) => res.data,
      providesTags: ['ProposalMasters'],
    }),
    createExclusion: builder.mutation<ExclusionItem, CreateExclusionInput>({
      query: (body) => ({ url: '/api/v1/proposals/exclusions', method: 'POST', body }),
      transformResponse: (res: ApiResponse<ExclusionItem>) => res.data,
      invalidatesTags: ['ProposalMasters'],
    }),
    updateExclusion: builder.mutation<ExclusionItem, UpdateExclusionInput>({
      query: ({ exclusion_code, ...body }) => ({
        url: `/api/v1/proposals/exclusions/${exclusion_code}`, method: 'PATCH', body,
      }),
      transformResponse: (res: ApiResponse<ExclusionItem>) => res.data,
      invalidatesTags: ['ProposalMasters'],
    }),
    deleteExclusion: builder.mutation<void, string>({
      query: (code) => ({ url: `/api/v1/proposals/exclusions/${code}`, method: 'DELETE' }),
      invalidatesTags: ['ProposalMasters'],
    }),
    listWarrantyTerms: builder.query<WarrantyTerm[], { includeInactive?: boolean } | void>({
      query: (arg) => `/api/v1/proposals/warranty-terms${arg?.includeInactive ? '?include_inactive=true' : ''}`,
      transformResponse: (res: ApiResponse<WarrantyTerm[]>) => res.data,
      providesTags: ['ProposalMasters'],
    }),
    createWarrantyTerm: builder.mutation<WarrantyTerm, CreateWarrantyTermInput>({
      query: (body) => ({ url: '/api/v1/proposals/warranty-terms', method: 'POST', body }),
      transformResponse: (res: ApiResponse<WarrantyTerm>) => res.data,
      invalidatesTags: ['ProposalMasters'],
    }),
    updateWarrantyTerm: builder.mutation<WarrantyTerm, UpdateWarrantyTermInput>({
      query: ({ warranty_term_code, ...body }) => ({
        url: `/api/v1/proposals/warranty-terms/${warranty_term_code}`, method: 'PATCH', body,
      }),
      transformResponse: (res: ApiResponse<WarrantyTerm>) => res.data,
      invalidatesTags: ['ProposalMasters'],
    }),
    deleteWarrantyTerm: builder.mutation<void, string>({
      query: (code) => ({ url: `/api/v1/proposals/warranty-terms/${code}`, method: 'DELETE' }),
      invalidatesTags: ['ProposalMasters'],
    }),
    listMarketingReferences: builder.query<MarketingReference[], void>({
      query: () => '/api/v1/proposals/marketing-references',
      transformResponse: (res: ApiResponse<MarketingReference[]>) => res.data,
      providesTags: ['ProposalMasters'],
    }),
    createMarketingReference: builder.mutation<MarketingReference, CreateMarketingReferenceInput>({
      query: (body) => ({ url: '/api/v1/proposals/marketing-references', method: 'POST', body }),
      transformResponse: (res: ApiResponse<MarketingReference>) => res.data,
      invalidatesTags: ['ProposalMasters'],
    }),
  }),
});

export const {
  useGetCurrentProposalQuery,
  useUpdateProposalSectionMutation,
  useGetProposalAcceptanceQuery,
  useRecordProposalAcceptanceMutation,
  useSetRecommendedOptionMutation,
  useSetOptionIncludedMutation,
  useChangeProposalTemplateMutation,
  useLazyGetProposalPdfQuery,
  useListProposalTemplatesQuery,
  useListProposalSectionDefinitionsQuery,
  useListPaymentTermsQuery,
  useCreatePaymentTermMutation,
  useUpdatePaymentTermMutation,
  useDeletePaymentTermMutation,
  useListTermClausesQuery,
  useCreateTermClauseMutation,
  useUpdateTermClauseMutation,
  useDeleteTermClauseMutation,
  useListExclusionsQuery,
  useCreateExclusionMutation,
  useUpdateExclusionMutation,
  useDeleteExclusionMutation,
  useListWarrantyTermsQuery,
  useCreateWarrantyTermMutation,
  useUpdateWarrantyTermMutation,
  useDeleteWarrantyTermMutation,
  useListMarketingReferencesQuery,
  useCreateMarketingReferenceMutation,
} = proposalsApi;
